import { useState, type FC } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import { Chip, typographyVariants } from '@mint-lib/tw-components';

import {
  ApprovalProcessStatuses,
  ApprovalProcessStatusesEnum,
} from '../../constants/shared';
import { EntityStatuses } from '../../constants/shared.js';
import StatusDetailsModal from '../modals/StatusDetailsModal/StatusDetailsModal.jsx';

type Props = {
  approvalProcessId: string | null;
  userStatus: EntityStatuses;
  approvalProcessStatus: ApprovalProcessStatuses;
};

const getChipMetaMapByProcessStatus = (t: TFunction) => {
  return {
    [ApprovalProcessStatusesEnum.enum.draft]: null,
    [ApprovalProcessStatusesEnum.enum.pending]: {
      variant: 'outline',
      size: 'medium',
      color: 'info',
      label: t('Pending'),
    },
    [ApprovalProcessStatusesEnum.enum.rejected]: {
      variant: 'outline',
      size: 'medium',
      color: 'error',
      label: t('Rejected'),
    },
    [ApprovalProcessStatusesEnum.enum.approved]: {
      variant: 'outline',
      size: 'medium',
      color: 'success',
      label: t('Approved'),
    },
  } as const;
};

const CommonVariant: FC<Props> = ({
  approvalProcessId,
  userStatus,
  approvalProcessStatus,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const chipMetaMap = getChipMetaMapByProcessStatus(t);
  const chipProps = chipMetaMap[approvalProcessStatus];

  const showModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (!chipProps) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <span
          className={typographyVariants({
            variant: 'caption1',
            color: 'secondary',
          })}
        >
          {t('Status')}:
        </span>
        <Chip {...chipProps} className="cursor-pointer" onClick={showModal}>
          <Information />
          {chipProps.label}
        </Chip>
      </div>
      <StatusDetailsModal
        open={isModalOpen}
        onClose={closeModal}
        approvalProcessId={approvalProcessId}
        userStatus={userStatus}
      />
    </>
  );
};

export default CommonVariant;
